import { z } from "zod";
import { localized } from "../../locales";
import { guardNumber } from "../../numbers";

export const orderFormArticlesItemSchema = z.object({
	articleId: z.uuid(),
	quantity: z.coerce.number().min(0),
});

export const orderFormArticlesSchema = z.array(orderFormArticlesItemSchema);

export const orderFormLoadCarriersItemSchema = z.object({
	loadCarrierId: z.uuid(),
	quantity: z.coerce.number().min(0),
});

/**
 * The empty load carriers an order asks for — "Leergut": pallets and boxes shipped as goods
 * rather than as the vehicle for goods.
 *
 * Ordering these and the carriers a picking round attaches for transport draw on the same yard,
 * and they are the same pallets rather than two piles: a site that orders eighteen boxes and
 * needs twenty-four to travel gets twenty-four, not forty-two. So the quantity here is a **floor**
 * on what the round picks, which is why nothing in this file bounds it against transport.
 */
export const orderFormLoadCarriersSchema = z.array(orderFormLoadCarriersItemSchema);

export const orderFormSchema = z
	.object({
		projectId: z.uuid(),
		orderFileIds: z.array(z.string().nullish()).nullish(),
		deliveryDate: z.string(),
		articles: orderFormArticlesSchema,
		loadCarriers: orderFormLoadCarriersSchema.default([]),
		note: z.string().nullish(),
		tourWaveId: z.preprocess((val) => {
			if (val === "default") return null;
			return val;
		}, z.uuid().nullable()),
	})
	.superRefine((values, context) => {
		// An order has to ask for *something*, but Leergut on its own is a complete order: a site
		// that needs eighteen empty boxes and no goods is an ordinary request, and it derives its
		// own zoneless picking round. So the article requirement only bites when there is no
		// Leergut either.
		const hasArticleQuantities = values.articles.some((a) => guardNumber(a.quantity) > 0);
		const hasLoadCarrierQuantities = values.loadCarriers.some((c) => guardNumber(c.quantity) > 0);
		if (!hasArticleQuantities && !hasLoadCarrierQuantities) {
			context.addIssue({
				code: "custom",
				path: ["articles"],
				message: localized("stock.select_articles"),
			});
		}
	});

export type OrderFormArticlesItemSchema = z.infer<typeof orderFormArticlesItemSchema>;
export type OrderFormArticlesSchema = z.infer<typeof orderFormArticlesSchema>;
export type OrderFormLoadCarriersItemSchema = z.infer<typeof orderFormLoadCarriersItemSchema>;
export type OrderFormLoadCarriersSchema = z.infer<typeof orderFormLoadCarriersSchema>;
export type OrderFormSchema = z.infer<typeof orderFormSchema>;
